import { bindActionCreators, compose } from 'redux';
import { BorderBox, Button, H2, P, Slider, Spinner } from '@ahmdigital/ui';
import { connect } from 'react-redux';
import { forEach, isNull, keys, omitBy, toLower } from 'lodash/fp';
import formatCurrency from '@ahmdigital/logic/lib/claim/format-currency';
import moment from 'moment';
import React, { PureComponent } from 'react';

import { marks, percentages, productId, scale, staticFrequency } from './constants';
import { RootState } from '../../../../reducers';
import AllProductsDataLoader from '../../../../components/all-products-data-loader';
import config from '../../../../config';
import constants from '../../../../ahm-constants';
import CustomerActions from '../../../../actions/customer';
import getDateOfBirthFromAge from '../../../../utils/get-date-of-birth-from-age';
import getYouthDiscountSavings from '../../utils/get-youth-discount-savings';
import PremiumReviewToggle from '../../../../components/premium-review-toggle-wrapper';
import PriceActions from '../../../../actions/price';
import ProductActions from '../../../../actions/product';
import selectCustomer from '../../../../selectors/select-customer';
import styles from './styles.module.sass';
import tipFormatter from './components/tip-formatter';

const ages = keys(percentages);
const getFutureEffectiveDate = moment(config.get('features:premiumChange:futureEffectiveDate')).format(
  constants.DATE_FORMAT.ISO_8601,
);

// @ts-expect-error - Automatic, Please fix when editing this file
const getDefaultCustomerPriceData = (customer, age) => {
  // We force price request data to be consistent, ignoring what
  // the user might have in redux state
  const output = {
    ageBracket: constants.REBATE_AGE_BRACKET.BRACKET_0,
    frequency: staticFrequency,
    incomeTier: constants.INCOME_TIER.TIER_0,
    incomeType: constants.INCOME_TYPE.SINGLE,
    rebate: false,
    scale,
    state: customer.state,
  };
  // @ts-expect-error - Automatic, Please fix when editing this file
  if (percentages[age] > 0) {
    // @ts-expect-error - Automatic, Please fix when editing this file
    output.members = [
      {
        continuous: true,
        dob: getDateOfBirthFromAge(age),
        relationship: 'client',
      },
    ];
  }
  return output;
};

// @ts-expect-error - Automatic, Please fix when editing this file
const getPriceOptions = (useFutureEffectiveDate) => {
  const futureEffectiveDate = useFutureEffectiveDate ? getFutureEffectiveDate : null;
  return omitBy(isNull, {
    adjustedScale: true,
    date: futureEffectiveDate,
    lhc: false,
  });
};

type SliderBoxProps = {
  actions: {
    price: {
      load: (...args: unknown[]) => unknown;
    };
  };
  customer: {};
  prices: {};
  products: {};
  useFutureEffectiveDate: boolean;
};

class SliderBox extends PureComponent<SliderBoxProps> {
  // @ts-expect-error - Automatic, Please fix when editing this file
  static retrievePrices = (pricActions, customer, useFutureEffectiveDate) => {
    forEach((age) => {
      pricActions.load(
        productId,
        { customer: getDefaultCustomerPriceData(customer, age) },
        getPriceOptions(useFutureEffectiveDate),
      );
    }, ages);
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  state = {
    age: 18,
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  componentDidMount() {
    const { actions, customer, useFutureEffectiveDate } = this.props;
    SliderBox.retrievePrices(actions.price, customer, useFutureEffectiveDate);
  }

  // @ts-expect-error - Automatic, Please fix when editing this file
  componentDidUpdate(prevProps) {
    const { actions, customer, useFutureEffectiveDate } = this.props;

    if (useFutureEffectiveDate !== prevProps.useFutureEffectiveDate) {
      SliderBox.retrievePrices(actions.price, customer, useFutureEffectiveDate);
    }
  }

  // @ts-expect-error - Automatic, Please fix when editing this file
  onSliderChange = (age) => {
    this.setState({
      age,
    });
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  render() {
    const { customer, prices, products, useFutureEffectiveDate } = this.props;
    const { age } = this.state;

    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
    if (!products?.[productId]) {
      return null;
    }

    const customerData = getDefaultCustomerPriceData(customer, age);
    const savings = getYouthDiscountSavings({
      customer: customerData,
      frequency: staticFrequency,
      priceOptions: getPriceOptions(useFutureEffectiveDate),
      prices,
      productId,
    });

    return (
      <BorderBox variant={[styles.ageSelectionBox, 'thin']}>
        <>
          <H2 variant="marginBottomSpacer9">How much can I save on my hospital cover?</H2>
          {/* @ts-expect-error - Automatic, Please fix when editing this file */}
          <PremiumReviewToggle type="alternativePage" />
          <P>Select your age below to begin</P>
          {isNull(savings) ? (
            <Spinner variant={styles.spinner} />
          ) : (
            <>
              <div className={styles.slider}>
                <Slider
                  analytics={{ action: 'on_youth_discount_age_move_slider', component: 'youth_discount' }}
                  onChange={this.onSliderChange}
                  marks={marks}
                  min={18}
                  max={30}
                  tipFormatter={tipFormatter}
                  ariaLabelForHandle="discount"
                />
                <H2 looksLike="h4" variant={['marginBottomSpacer7', styles.results]}>
                  {savings === 0 ? (
                    'You don’t qualify for a discount, but we still have affordable cover available.'
                  ) : (
                    <>
                      If you’re {toLower(scale)} in {constants.STATE_LABEL[customerData.state]} you could save up to{' '}
                      {/* @ts-expect-error - Automatic, Please fix when editing this file */}
                      {formatCurrency(savings)} a year on {products[productId].name} product<sup>*</sup>
                    </>
                  )}
                </H2>
              </div>
              {savings > 0 && (
                <P variant={['marginBottomSpacer7', styles.disclaimer]} data-testid="disclaimer">
                  <sup>*</sup>Paying by direct debit. Australian Government Rebate is not applied. <br />
                  Hospital price includes an excess of $750 and excludes Lifetime Health Cover loading. <br />
                  Youth Discount only applies to hospital component.
                </P>
              )}
              <Button variant="sharp" to={constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL}>
                View all hospital cover
              </Button>
            </>
          )}
        </>
      </BorderBox>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  customer: selectCustomer(state),
  prices: state.price.data,
  products: state.product.data,
  useFutureEffectiveDate: state.price.useFutureEffectiveDate,
});

// @ts-expect-error - Automatic, Please fix when editing this file
const mapDispatchToProps = (dispatch) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
    price: bindActionCreators(PriceActions, dispatch),
    product: bindActionCreators(ProductActions, dispatch),
  },
});

export default compose(AllProductsDataLoader, connect(mapStateToProps, mapDispatchToProps))(SliderBox);

export { SliderBox as SliderBoxForTest, mapStateToProps };
